import { ArrowBack, Block, Delete, DisabledByDefault, Download, LockReset, Search as SearchIcon, SwapHoriz } from '@mui/icons-material';
import {
    Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions,
    DialogContent, DialogContentText, DialogTitle, IconButton, InputAdornment,
    Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, Typography, Tooltip, TablePagination, Switch, FormControlLabel
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import util from '../util';

const PAGE_SIZE = 50;

function formatBytes(mb) {
    if (mb === null || mb === undefined) return 'N/A';
    const val = Number(mb);
    if (!Number.isFinite(val)) return 'N/A';
    if (val >= 1024) return `${(val / 1024).toFixed(2)} GB`;
    return `${val.toFixed(2)} MB`;
}

function formatDate(isoStr) {
    if (!isoStr) return 'N/A';
    try {
        const d = new Date(isoStr);
        return d.toLocaleString();
    } catch {
        return isoStr;
    }
}

function UserDetail({ user, onRefresh }) {
    const navigate = useNavigate();
    const [disableReason, setDisableReason] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const [resetPassword, setResetPassword] = useState('');
    const [openDialog, setOpenDialog] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleDisable = async () => {
        if (!disableReason.trim()) return;
        setLoading(true);
        try {
            await util.postRequest(`/api/admin/user/${user.id}/disable`, { reason: disableReason });
            onRefresh();
        } finally {
            setLoading(false);
        }
    };

    const handleEnable = async () => {
        setLoading(true);
        try {
            await util.postRequest(`/api/admin/user/${user.id}/enable`);
            onRefresh();
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (deleteConfirm !== user.username) return;
        setLoading(true);
        try {
            await util.postRequest(`/api/admin/user/${user.id}/delete`);
            onRefresh();
        } finally {
            setLoading(false);
        }
    };

    const handleImpersonate = async () => {
        setLoading(true);
        try {
            const res = await util.postRequest(`/api/admin/user/${user.id}/impersonate`, {});
            if (res && res.token) {
                localStorage.setItem('token', res.token);
                localStorage.setItem('user', JSON.stringify(res.user));
                window.dispatchEvent(new Event('storage'));
                window.location.reload();
            }
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordReset = async () => {
        if (!resetPassword.trim()) return;
        setLoading(true);
        try {
            await util.postRequest(`/api/admin/user/${user.id}/password`, { newPassword: resetPassword });
            setOpenDialog(null);
            setResetPassword('');
        } finally {
            setLoading(false);
        }
    };

    const isAdmin = user.role === 'admin';

    return (
        <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <IconButton onClick={onRefresh}>
                    <ArrowBack />
                </IconButton>
                <Typography variant="h5" sx={{ ml: 1 }}>{user.username}</Typography>
                {user.role === 'admin' && (
                    <Chip label="Admin" color="primary" size="small" sx={{ ml: 1 }} />
                )}
                {user.isDisabled && (
                    <Chip label="Disabled" color="error" size="small" sx={{ ml: 1 }} />
                )}
            </Box>

            <Paper sx={{ p: 2, backgroundColor: '#1d1d1d', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ opacity: 0.7 }}>User Details</Typography>
                <Box sx={{ mt: 1, display: 'grid', gap: 0.5 }}>
                    <Typography>ID: {user.id}</Typography>
                    <Typography>Email: {user.email || 'N/A'}</Typography>
                    <Typography>Role: {user.role}</Typography>
                    <Typography>Concurrence: {user.concurrence}</Typography>
                    <Typography>Upload Chunk Concurrence: {user.uploadChunkConcurrence}</Typography>
                    <Typography>Thumbnails: {user.thumbnail ? 'Enabled' : 'Disabled'}</Typography>
                    <Typography>Registered: {formatDate(user.registeredAt)}</Typography>
                    <Typography>Last Seen: {formatDate(user.lastSeen)}</Typography>
                    <Typography>Storage Used: {formatBytes(user.storageMb)}</Typography>
                    <Typography>Files: {user.filesCount || 0} • Folders: {user.foldersCount || 0}</Typography>
                </Box>
            </Paper>

            {(user.disableReason || user.disabledAt) && (
                <Paper sx={{ p: 2, backgroundColor: '#1d1d1d', mb: 2 }}>
                    <Typography variant="subtitle1" color="error">Disabled Info</Typography>
                    <Box sx={{ mt: 1 }}>
                        <Typography>Reason: {user.disableReason || 'None'}</Typography>
                        <Typography>Disabled At: {formatDate(user.disabledAt)}</Typography>
                    </Box>
                </Paper>
            )}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {!user.isDisabled && !isAdmin ? (
                    <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<DisabledByDefault />}
                        onClick={() => setOpenDialog('disable')}
                    >
                        Disable Account
                    </Button>
                ) : user.isDisabled && !isAdmin ? (
                    <Button
                        variant="outlined"
                        color="success"
                        startIcon={<Download />}
                        onClick={handleEnable}
                        disabled={loading}
                    >
                        Re-enable Account
                    </Button>
                ) : user.isDisabled ? (
                    <Chip label="Cannot modify admin status" size="small" />
                ) : null}

                {user.role !== 'admin' && !user.isDisabled ? (
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<SwapHoriz />}
                        onClick={handleImpersonate}
                        disabled={loading}
                    >
                        Impersonate
                    </Button>
                ) : user.role !== 'admin' && user.isDisabled ? (
                    <Chip label="Cannot impersonate disabled account" size="small" sx={{ mr: 1 }} />
                ) : null}

                {user.role !== 'admin' ? (
                    <Button
                        variant="outlined"
                        color="info"
                        startIcon={<LockReset />}
                        onClick={() => setOpenDialog('password')}
                        disabled={loading}
                    >
                        Reset Password
                    </Button>
                ) : null}

                {user.role !== 'admin' ? (
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<Delete />}
                        onClick={() => setOpenDialog('delete')}
                        disabled={loading}
                    >
                        Delete Account
                    </Button>
                ) : (
                    <Chip label="Cannot delete admin account" size="small" sx={{ mr: 1 }} />
                )}

                {user.role !== 'admin' && <Tooltip title="Admin actions restricted for other admins">
                    <Box sx={{ display: 'none' }} />
                </Tooltip>}
            </Box>

            <Dialog open={openDialog === 'disable'} onClose={() => setOpenDialog(null)}>
                <DialogTitle>Disable Account</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to disable {user.username}? This will invalidate all active sessions.
                        A reason is required.
                    </DialogContentText>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Reason"
                        value={disableReason}
                        onChange={(e) => setDisableReason(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(null)}>Cancel</Button>
                    <Button
                        color="warning"
                        variant="contained"
                        disabled={!disableReason.trim() || loading}
                        onClick={handleDisable}
                    >
                        Disable
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openDialog === 'delete'} onClose={() => setOpenDialog(null)}>
                <DialogTitle>Delete Account</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will permanently delete {user.username} and all associated data. Type the username to confirm.
                    </DialogContentText>
                    <TextField
                        fullWidth
                        label={`Type "${user.username}" to confirm`}
                        value={deleteConfirm}
                        onChange={(e) => setDeleteConfirm(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(null)}>Cancel</Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={deleteConfirm !== user.username || loading}
                        onClick={handleDelete}
                    >
                        Delete Permanently
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openDialog === 'password'} onClose={() => setOpenDialog(null)}>
                <DialogTitle>Reset Password</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Enter a new password for {user.username}. Note: changing the password does not invalidate existing sessions. Use disable/enable to force them out first if needed.
                    </DialogContentText>
                    <TextField
                        fullWidth
                        type="password"
                        label="New Password"
                        value={resetPassword}
                        onChange={(e) => setResetPassword(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(null)}>Cancel</Button>
                    <Button
                        color="info"
                        variant="contained"
                        disabled={!resetPassword.trim() || loading}
                        onClick={handlePasswordReset}
                    >
                        Reset Password
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

const AdminPage = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [offset, setOffset] = useState(0);
    const [totalUsers, setTotalUsers] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    const [allowRegistrations, setAllowRegistrations] = useState(null);
    const [regLoading, setRegLoading] = useState(true);

    const fetchUsers = useCallback(async (off = 0, term = '') => {
        setLoading(true);
        setError('');
        try {
            let url = `/api/admin/users?offset=${off}&limit=${PAGE_SIZE}`;
            if (term) url += `&search=${encodeURIComponent(term)}`;
            const res = await util.getRequest(url);
            if (!res || res.code === 401 || res.code === 500) {
                throw new Error('Failed to fetch users');
            }
            setUsers(res.data || []);
            setTotalUsers(res.totalUsers || 0);
            setHasMore(!!res.hasMore);
        } catch (e) {
            setError(e.message || 'Failed to fetch users');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchRegistrations = useCallback(async () => {
        setRegLoading(true);
        try {
            const res = await util.getRequest('/api/admin/registrations');
            if (res) {
                setAllowRegistrations(res.allowRegistrations);
            }
        } catch {
            // silent fail
        } finally {
            setRegLoading(false);
        }
    }, []);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user') || 'false');
        if (!user || user.role !== 'admin') {
            navigate('/contentManager');
            return;
        }
        fetchUsers();
        fetchRegistrations();
    }, []);

    const debouncedSearch = useMemo(() => {
        let timer;
        return (val) => {
            clearTimeout(timer);
            setSearchInput(val);
            timer = setTimeout(() => {
                setSearch(val);
                setOffset(0);
                fetchUsers(0, val);
            }, 300);
        };
    }, [fetchUsers]);

    const handleToggleRegistrations = async () => {
        try {
            const newVal = !allowRegistrations;
            await util.postRequest('/api/admin/registrations', { allowRegistrations: newVal });
            setAllowRegistrations(newVal);
        } catch {
            // handled by util snackCaller
        }
    };

    if (selectedUser) {
        return <UserDetail user={selectedUser} onRefresh={() => { setSelectedUser(null); fetchUsers(offset, search); }} />;
    }

    return (
        <Box sx={{ mt: 2, p: 2, backgroundColor: '#151515', borderRadius: 1, minHeight: 'calc(100vh - 110px)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <IconButton onClick={() => navigate('/contentManager')}>
                    <ArrowBack />
                </IconButton>
                <Typography variant="h5" sx={{ ml: 1 }}>Admin Panel</Typography>

                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<Block />}
                        onClick={() => navigate('/admin/bans')}
                    >
                        Blocked IPs
                    </Button>
                    {!regLoading && allowRegistrations !== null && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={allowRegistrations}
                                    onChange={handleToggleRegistrations}
                                    color="primary"
                                />
                            }
                            label={`Registrations: ${allowRegistrations ? 'Open' : 'Closed'}`}
                        />
                    )}
                </Box>
            </Box>

            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by username or email..."
                    value={searchInput}
                    onChange={(e) => debouncedSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                <Button variant="outlined" onClick={() => { setSearch(''); setOffset(0); fetchUsers(0, ''); }}>Clear</Button>
            </Box>

            {loading ? (
                <Box sx={{ mt: '25vh', textAlign: 'center' }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : (
                <>
                    <TableContainer component={Paper} sx={{ backgroundColor: '#1d1d1d' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Username</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Role</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Storage</TableCell>
                                    <TableCell align="right">Files</TableCell>
                                    <TableCell align="right">Folders</TableCell>
                                    <TableCell>Last Seen</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center' }}>
                                            No users found.
                                        </TableCell>
                                    </TableRow>
                                )}
                                {users.map((u) => (
                                    <TableRow
                                        key={u.id}
                                        onClick={() => setSelectedUser(u)}
                                        sx={{
                                            cursor: 'pointer',
                                            backgroundColor: u.isDisabled ? '#2a1010' : 'transparent',
                                            '&:hover': { backgroundColor: '#333' },
                                        }}
                                    >
                                        <TableCell>
                                            <Typography component="span" fontWeight={u.role === 'admin' ? 700 : 400}>
                                                {u.username}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{u.email || '-'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={u.role}
                                                size="small"
                                                color={u.role === 'admin' ? 'primary' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {u.isDisabled ? (
                                                <Chip label="Disabled" size="small" color="error" />
                                            ) : (
                                                <Chip label="Active" size="small" color="success" />
                                            )}
                                        </TableCell>
                                        <TableCell align="right">{formatBytes(u.storageMb)}</TableCell>
                                        <TableCell align="right">{u.filesCount || 0}</TableCell>
                                        <TableCell align="right">{u.foldersCount || 0}</TableCell>
                                        <TableCell>{formatDate(u.lastSeen)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ opacity: 0.7 }}>
                            Showing {users.length} of {totalUsers} users{search ? ` (filtered by "${search}")` : ''}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                size="small"
                                disabled={offset === 0 || loading}
                                onClick={() => { setOffset((o) => Math.max(0, o - PAGE_SIZE)); fetchUsers(Math.max(0, offset - PAGE_SIZE), search); }}
                            >
                                Previous
                            </Button>
                            <Button
                                size="small"
                                disabled={!hasMore || loading}
                                onClick={() => { setOffset((o) => o + PAGE_SIZE); fetchUsers(offset + PAGE_SIZE, search); }}
                            >
                                Next
                            </Button>
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    );
};

export default AdminPage;
